import { useState } from 'react';
import styled from 'styled-components';
import { IoSendOutline, IoPersonOutline } from 'react-icons/io5';
import InfoTooltip from '../../../../components/common/InfoTooltip';
import { requestShare } from '../../../../bridge/webBridge';
import { useAuth } from '../../../../context/AuthContext';

const buildSignupUrl = (ref) => {
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://ieum-8fd99.web.app';
  return `${origin}/realtor/signup?ref=${ref || 'demo'}`;
};

const RealtorInviteSection = () => {
  const { userData } = useAuth();
  const [method, setMethod] = useState('direct');
  const [phone, setPhone] = useState('');
  const signupUrl = buildSignupUrl(userData?.referralCode || userData?.uid);

  const handleInvite = () => {
    if (!phone.replace(/\D/g, '')) {
      alert('휴대폰 번호를 입력해주세요.');
      return;
    }
    const rawPhone = phone.replace(/\D/g, '');
    const msg = `이음에 함께하세요! 가입하시면 추천인이 자동 등록됩니다.\n${signupUrl}`;
    window.location.href = `sms:${rawPhone}?body=${encodeURIComponent(msg)}`;
  };

  return (
    <section>
      <FormCard>
        <SectionTitle>공인중개사 초대 <InfoTooltip text="동료 공인중개사 분들에게 이음을 추천해주세요. 해당 공인중개사님이 초청을 받아 해당 링크로 이음에 가입하면 그 공인중개사 마케팅 수익의 5%를 1년 동안 받으실 수 있습니다." /></SectionTitle>
        <MethodTabs>
          <MethodTab $active={method === 'direct'} onClick={() => setMethod('direct')}>직접입력</MethodTab>
          <MethodTab $active={method === 'contacts'} onClick={() => setMethod('contacts')}>주소록</MethodTab>
          <MethodTab $active={method === 'kakao'} onClick={() => setMethod('kakao')}>카카오친구</MethodTab>
        </MethodTabs>

        {method === 'direct' && (
          <>
            <Desc>휴대폰 번호 입력 시 고정 문구와 앱 가입 링크 (추천인 자동등록)가 발송됩니다.</Desc>
            <PhoneInput
              placeholder="휴대번호 '-'없이 입력"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              inputMode="tel"
            />
            <InviteBtn onClick={handleInvite}>
              <IoSendOutline size={16} /> 초대하기
            </InviteBtn>
          </>
        )}

        {method === 'contacts' && (
          <>
            <Desc>주소록에서 연락처를 선택하면 번호가 자동으로 입력됩니다.</Desc>
            <ContactLink onClick={() => {
              if (window.ReactNativeWebView) {
                window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'OPEN_CONTACTS', payload: {} }));
              } else {
                alert('앱에서만 사용 가능합니다.');
              }
            }}>
              <IoPersonOutline size={16} /> 주소록에서 선택
            </ContactLink>
            {phone && (
              <>
                <PhoneInput value={phone} readOnly />
                <InviteBtn onClick={handleInvite}>
                  <IoSendOutline size={16} /> 초대하기
                </InviteBtn>
              </>
            )}
          </>
        )}

        {method === 'kakao' && (
          <>
            <Desc>카카오톡 친구에게 초대 메시지를 보냅니다.</Desc>
            <InviteBtn onClick={() => {
              const shared = requestShare({
                title: '이음 - 공인중개사 파트너 초대',
                text: '이음에 가입하시면 마케팅 수익을 함께 나눌 수 있습니다.\n\n' + signupUrl,
              });
              if (!shared) {
                navigator.clipboard.writeText('이음에 가입하시면 마케팅 수익을 함께 나눌 수 있습니다.\n\n' + signupUrl).then(() => {
                  alert('링크가 클립보드에 복사되었습니다.\n카카오톡에 붙여넣기 해주세요.');
                });
              }
            }} style={{ background: '#FEE500', color: '#3C1E1E' }}>
              카카오톡으로 초대하기
            </InviteBtn>
          </>
        )}
      </FormCard>
    </section>
  );
};

export default RealtorInviteSection;

// ───── 스타일 (InviteRealtor 의 동일 styled 복사) ─────

const FormCard = styled.div`
  background: #fff;
  border-radius: ${({ theme }) => theme.radius.lg};
  padding: ${({ theme }) => theme.spacing.lg};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
`;

const SectionTitle = styled.h2`
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const MethodTabs = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.xs};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const MethodTab = styled.button`
  flex: 1;
  padding: 8px 12px;
  font-family: inherit;
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.textSecondary)};
  background: ${({ $active, theme }) => ($active ? `${theme.colors.primary}15` : '#fff')};
  border: 1px solid ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.border)};
  border-radius: ${({ theme }) => theme.radius.md};
  cursor: pointer;
`;

const Desc = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const PhoneInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  font-family: inherit;
  font-size: ${({ theme }) => theme.fontSize.base};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  margin-bottom: ${({ theme }) => theme.spacing.md};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const InviteBtn = styled.button`
  width: 100%;
  padding: 14px;
  font-family: inherit;
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: #fff;
  background: ${({ theme }) => theme.colors.primary};
  border: 0;
  border-radius: ${({ theme }) => theme.radius.md};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const ContactLink = styled.button`
  width: 100%;
  padding: 12px;
  font-family: inherit;
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => `${theme.colors.primary}10`};
  border: 1px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.radius.md};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.xs};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;
